// Pure call-outcome computation for Vonage counselling sessions (POD2-2117).
// The hook accumulates raw signals in a metrics ref during the session; at
// session end we reduce them to exactly one outcome + reason for reporting.
// Mirrors inkblot-web/src/components/Session/video/callOutcome.ts.

export const createCallMetrics = () => ({
  // performance.now() timestamps (ms); null until the signal fires.
  joinAttemptedAt: null,
  joinedAt: null,
  publishedAt: null,
  firstRemoteAt: null,
  hadReconnect: false,
  // OpenTok error code (e.g. 'OT_TIMEOUT') from a fatal area, if any.
  fatalErrorCode: null,
  // Latched true once the corresponding Vonage event fires; never reset.
  remoteConnectionSeen: false,
  remoteStreamSeen: false,
});

// A call is "settled" once its outcome can no longer improve: either two-way
// audio was established (success path) or a fatal error was recorded (failure
// path). Used to decide whether a REVERSIBLE signal (visibilitychange:hidden)
// should finalize the one-shot outcome. Genuine page-away signals (pagehide,
// unmount) finalize unconditionally; a mere tab background mid-connect must not,
// or a call that later succeeds would be permanently recorded as failed.
export const isCallSettled = metrics =>
  (metrics.publishedAt != null && metrics.firstRemoteAt != null) ||
  metrics.fatalErrorCode != null;

// Maps an OpenTok error code to a coarse reason category. Returns null when
// the code is unrecognised so the caller can fall back to a context default.
export const mapReason = code => {
  switch (code) {
    case 'OT_USER_MEDIA_ACCESS_DENIED':
      return 'permission';
    case 'OT_HARDWARE_UNAVAILABLE':
    case 'OT_NO_DEVICES_FOUND':
      return 'device';
    case 'OT_TIMEOUT':
      return 'timeout';
    case 'OT_CONNECT_FAILED':
    case 'OT_NOT_CONNECTED':
    case 'OT_STREAM_CREATE_FAILED':
    case 'OT_UNABLE_TO_PUBLISH':
    case 'OT_ICE_WORKFLOW_FAILED':
      return 'network';
    case 'OT_SET_REMOTE_DESCRIPTION_FAILED':
    case 'OT_CREATE_PEER_CONNECTION_FAILED':
      return 'av_connection';
    default:
      return null;
  }
};

const diff = (start, end) =>
  start != null && end != null ? Math.round(end - start) : null;

export const computeCallOutcome = metrics => {
  // Two-way audio requires BOTH the local publish to succeed and at least one
  // named remote stream to be subscribed. A SIP/voice leg has no name and does
  // not count as a real participant.
  const publishSucceeded = metrics.publishedAt != null;
  const twoWayAudio = publishSucceeded && metrics.firstRemoteAt != null;

  const timings = {
    timeToConnectMs: diff(metrics.joinAttemptedAt, metrics.joinedAt),
    timeToTwoWayAudioMs: diff(metrics.joinAttemptedAt, metrics.firstRemoteAt),
  };

  const signals = {
    remoteConnectionSeen: metrics.remoteConnectionSeen,
    remoteStreamSeen: metrics.remoteStreamSeen,
    publishSucceeded,
  };

  if (twoWayAudio) {
    return {
      outcome: metrics.hadReconnect ? 'completed_with_issues' : 'successful',
      reason: null,
      timings,
      signals,
    };
  }

  // Requiring a healthy local side (published, no fatal error) is what keeps
  // genuine local failures in `failed` rather than masking them as no-shows.
  const noShow =
    metrics.fatalErrorCode == null &&
    publishSucceeded &&
    !metrics.remoteConnectionSeen;

  if (noShow) {
    return { outcome: 'no_show', reason: null, timings, signals };
  }

  return {
    outcome: 'failed',
    reason:
      mapReason(metrics.fatalErrorCode) ??
      (metrics.hadReconnect ? 'network' : 'unknown'),
    timings,
    signals,
  };
};
